
// Vuex
import { Component, Watch, Mixins, Prop } from "vue-property-decorator";
import { namespace, Getter, Action } from "vuex-class";

import { AppRoutes } from "@/router";

// Components
import AreaNotesFinder from "@/components/area-notes/AreaNotesFinder.vue";
import LinkedTaskModal from "@/components/alarm-queue/LinkedTaskModal.vue";
import SystemQueue from "./event-queue/SystemQueue.vue";
import SiteArmingDialog from "@/components/arm-disarm/SiteArmingDialog.vue";
import Multiselect from "vue-multiselect";
import ShortcutHelp from "./ShortcutHelp.vue";
import AlarmQueueFilterEdit from "@/components/AlarmQueueFilterEdit.vue";
import NavMenu from "@/components/NavMenu.vue";
import NavHeader from "@/components/NavHeader.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { Dropdown, DropdownItem } from "@sureview/vuejs-sv-dropdown";

// Utils
import { AxiosResponse } from "axios";
import { get } from "lodash";
import RegionalHandlerMixin from "@/mixins/RegionalHandlerMixin";
import { formatRegionUrl } from "@/utils/string-utils";
import Intercom from "./event-queue/Intercom.vue";

// Types
import { MetadataRequest } from "../store/metadata/types";
import { UserPermissions, FeaturesList } from "@/store/types";
import {
	AlarmQueueFilterID,
	FilteredEvent,
	AlarmQueueFilter,
	AlarmQueueFilterEditModel
} from "@/store/eventqueue/types";

import AlarmAlert from "@/components/AlarmAlert.vue";
import { ArmDisarmState } from "@/store/arm-disarm-area/types";

// Vuex Modules
const Eventqueue = namespace("eventqueue");
const AlarmPoints = namespace("alarmPoints");
const ManualRaiseStore = namespace("manualRaise");
const Metadata = namespace("metadata");
const ArmDisarm = namespace("armDisarm");

@Component({
	components: {
		multiselect: Multiselect,
		"shortcut-help": ShortcutHelp,
		VuePerfectScrollbar: VuePerfectScrollbar,
		"alarm-queue-filter-edit": AlarmQueueFilterEdit,
		"nav-menu": NavMenu,
		"nav-header": NavHeader,
		"area-notes-finder": AreaNotesFinder,
		"linked-task-modal": LinkedTaskModal,
		"system-queue": SystemQueue,
		intercom: Intercom,
		"site-arming-dialog": SiteArmingDialog,
		dropdown: Dropdown,
		"dropdown-item": DropdownItem,
		"alarm-alert": AlarmAlert
	}
})
export default class AlarmQueueHeader extends Mixins(RegionalHandlerMixin) {
	@Prop({ type: Boolean, default: true }) showMenu;
	@Prop({ type: Boolean, default: false }) suppressAudioAlerts: boolean;
	@Prop({ type: Boolean, default: false }) suppressVisualAlerts: boolean;

	$refs!: {
		deleteFilterConfirmation: any;
		areaNotesFinder: AreaNotesFinder;
		alarmAlert: AlarmAlert;
	};

	@Eventqueue.Getter("getActiveEvents") events!: FilteredEvent[];
	@Eventqueue.Getter("getActiveFilterID") currentFilterID!: AlarmQueueFilterID;
	@Eventqueue.Getter("getAlarmQueueFilters") activeFilters!: AlarmQueueFilter[];
	@Eventqueue.Getter("getLastUpdatedEvents") eventsLastUpdated!: number;
	@Eventqueue.Getter("getUserZone") userZone!: any;
	@Eventqueue.Getter("getBulkHandleShown") bulkHandleShown!: boolean;
	@Eventqueue.Getter getHandlingEvent: boolean;

	@Eventqueue.Mutation setActivityLogShown: (shown: boolean) => void;
	@Eventqueue.Mutation setManualPatrolShown: (shown: boolean) => void;
	@Eventqueue.Mutation setManualActionShown: (shown: boolean) => void;
	@Eventqueue.Mutation setNewFilterModalShown: (shown: boolean) => void;
	@Eventqueue.Mutation setLinkedTaskModalShown: ({ visible, responsesMetadata }) => void;
	@Eventqueue.Mutation setBulkHandleShown: (shown: boolean) => void;
	@Eventqueue.Mutation setSelectedAlarms: (data: number[]) => void;
	@Eventqueue.Mutation setAllAlarmsSelected: (allSelected: boolean) => void;
	@Eventqueue.Mutation setHandlingEvent: (handlingEvent: boolean) => void;
	@Eventqueue.Mutation setJoiningEventID: (eventId: number) => void;

	@Eventqueue.Action setActiveFilter: any;
	@Eventqueue.Action fetchAlarmQueueFilters: any;
	@Eventqueue.Action fetchEventQueue: (filters?: AlarmQueueFilterID[]) => Promise<void>;
	@Eventqueue.Action loadAvailableAlarmQueueFilters: any;
	@Eventqueue.Action addAlarmQueueFilter: any;
	@Eventqueue.Action removeAlarmQueueFilter: any;
	@Eventqueue.Action eventHandleTop: any;
	@Eventqueue.Action deleteAlarmQueueFilter: any;
	@Eventqueue.Action setHideMapFlag: any;

	// Getter and action for toggling the map' display.
	@Eventqueue.Getter("getHideMapFlag") hideMapFlag: boolean;

	@AlarmPoints.Mutation setAlarmPointManagerOpen;
	@ManualRaiseStore.Mutation setManualRaiseShown: (visible: boolean) => void;

	@Metadata.Action loadMetadataTypes: () => Promise<void>;
	@Metadata.Action loadMetadata: (metadataRequest: MetadataRequest) => Promise<void>;

	@ArmDisarm.Mutation setArmDisarmShown: (visible: boolean) => void;
	@ArmDisarm.State((state: ArmDisarmState) => state.armDisarmShown) private showArmDisarm: boolean;

	@Getter getPermissions: UserPermissions;
	@Getter getFeature: (featureNames: string[]) => boolean;
	@Getter("getFeaturesList") featuresList: FeaturesList;

	@Action serverLogout: any;
	@Action logout: () => void;

	public updateInterval: any;
	public stopped = false;
	public allAvailableFilters: AlarmQueueFilter[] = [];

	public filterSearch: string = "";

	public modalShow = false;

	public addingFilter = false;
	public filterToDelete = {};
	public editedFilter: AlarmQueueFilterEditModel = null;
	public updateFilter: boolean = false;

	private isAreaNotesMousedOver = false;

	public filterShortcuts = {
		1: ["ctrl", "1"],
		2: ["ctrl", "2"],
		3: ["ctrl", "3"],
		4: ["ctrl", "4"],
		5: ["ctrl", "5"],
		6: ["ctrl", "6"],
		7: ["ctrl", "7"],
		8: ["ctrl", "8"],
		9: ["ctrl", "9"],
		0: ["ctrl", "0"]
	};

	private get isQuickControlEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "QuickControl"]) && this.getPermissions.canAccessQuickControl;
	}

	get activeEventsCount(): number {
		return this.activeFilters.length ? this.activeFilters[0].activeAlarmCount : 0;
	}

	get systemEventsCount(): number {
		if (!this.canViewSystemEvents) {
			return 0;
		}

		return this.events.filter((q: FilteredEvent) => q.eventTypeID == 1 && !q.groupID).length;
	}

	get shouldSuppressAlert(): boolean {
		// only surpress the alert of we have no alarms and no system events
		return !(this.activeEventsCount > 0) && this.systemEventsCount == 0;
	}

	get shouldSuppressVisualAlert(): boolean {
		// only show the visual alert for alarms and not system events (as per v1)
		return this.suppressVisualAlerts || this.activeEventsCount == 0;
	}

	get shouldSuppressAudioAlert(): boolean {
		// supress the audio alert if there are no alarms or system events
		return this.suppressAudioAlerts || (this.activeEventsCount == 0 && this.systemEventsCount == 0);
	}

	public get visibleFilters() {
		if (this.activeFilters && this.activeFilters.length > 0) {
			return this.activeFilters.filter(filter => !filter.hide);
		}

		return [];
	}

	public get availableFilters() {
		return this.allAvailableFilters.filter(queueFilter => {
			return (
				this.activeFilters.findIndex(
					activeFilter =>
						activeFilter.alarmQueueFilterID === queueFilter.alarmQueueFilterID &&
						activeFilter.regionID === queueFilter.regionID
				) == -1
			);
		});
	}

	// Gets the filters and sorts then alphabetically
	public get filteredFilters() {
		const titleWithRegion = (filter: AlarmQueueFilter) =>
			filter.regionID > 0 ? `${filter.title} (${filter.regionTitle})` : filter.title;
		let search = this.filterSearch.toLowerCase();
		return this.availableFilters
			.filter(
				filter =>
					titleWithRegion(filter)
						.toLowerCase()
						.indexOf(search) > -1
			)
			.sort(function(a, b) {
				var first = a.title.toUpperCase();
				var second = b.title.toUpperCase();
				return first < second ? -1 : first > second ? 1 : 0;
			});
	}

	public get bulkHandleEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "EndMultipleEvents"]);
	}

	public get quickControlOpenNewWindow(): boolean {
		return get(this.featuresList, ["Alarms", "QuickControl", "OpenInNewWindow"]);
	}

	public get bulkHandlePermission(): boolean {
		return this.getPermissions.canEndMultipleAlarms;
	}

	public get alarmBulkHandleAllowed(): boolean {
		let featureEnabled: boolean = this.bulkHandleEnabled;
		let permissionEnabled: boolean = this.bulkHandlePermission;

		return featureEnabled && permissionEnabled;
	}

	public get alarmPointsEnabled() {
		return get(this.featuresList, ["Alarms", "AlarmPoints"]);
	}

	public get siteMonitorEnabled() {
		return get(this.featuresList, ["Alarms", "SiteMonitor"]);
	}

	public get isActivityLogEnabled() {
		return get(this.featuresList, ["ActivityLog"]);
	}

	private get isManualToursEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "ManualTour"]);
	}

	private get isDisplayInQueue(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "Intercom", "DisplayInQueue"]);
	}

	private get isDisplayInAlarmHeaderEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "Intercom", "DisplayInAlarmHeader"]);
	}

	private get isAreaNotesEnabled() {
		return get(this.featuresList, ["AreaNotes"]) && get(this.featuresList, ["AreaNotes", "Icons"]);
	}

	private get canArmDisarmSites(): boolean {
		const feature = this.getFeature(["Alarms", "EventQueue", "SeeSiteArmedState"]);
		return feature && this.getPermissions.canViewSiteArmedState;
	}
	private showSiteArmingDialog(): void {
		if (this.canArmDisarmSites) {
			this.setArmDisarmShown(true);
		}
	}

	// Feature flag for map display toggling functionality.
	private get isToggleMapEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "Maps", "ToggleEventQueue"]);
	}

	@Watch("eventsLastUpdated")
	public onEventsChanged() {
		if (this.events == null) {
			return;
		}

		const alarms = this.events.filter(
			q => q.eventTypeID == 1 && !q.inProcessing && q.unParkAt == null && q.isPermanentParked == false
		);
		const systemEvents = this.events.filter((q: FilteredEvent) => q.eventTypeID == 1 && !q.groupID);

		if (alarms.length > 0 || (this.canViewSystemEvents && systemEvents.length > 0)) {
			this.$refs.alarmAlert.triggerAlert();
		}
	}

	public async mounted() {
		this.hideConfirmationModal();
		//this.$refs.deleteFilterConfirmation.hide();
		this.stopped = false;

		await this.fetchAlarmQueueFilters();
		await this.setAllAvailableFilters();

		if (this.getFeature(["Integration", "Tasks"])) {
			await this.loadMetadataTypes();
		}

		// @refactor messy as hell, investigate a single call to `this.update` and
		// reduce number of mutaitons, re-work state to be less "flat",
		// complicated selections can be done with getters.
		this.updateInterval = setTimeout(this.update, 2000);
	}

	private async setAllAvailableFilters() {
		const filters = await this.loadAvailableAlarmQueueFilters();
		this.$set(this, "allAvailableFilters", filters);
	}

	public async update() {
		if (!this.stopped) {
			const before = performance.now();

			try {
				await this.fetchEventQueue();
				await this.fetchMetadataForEvents();
			} catch (ex) {
				console.error(ex);
			}

			const after = performance.now();
			const timeTaken = after - before;

			this.updateInterval = setTimeout(this.update, Math.max(10, 2000 - timeTaken));
		} else {
			if (this.updateInterval != null) {
				clearTimeout(this.updateInterval);
				this.updateInterval = null;
			}
		}
	}

	public destroyed() {
		this.stopped = true;
		if (this.updateInterval != null) {
			clearInterval(this.updateInterval);
			this.updateInterval = null;
		}
	}

	public async logoutUser() {
		await this.serverLogout();
		this.logout();
	}

	public async processTopAlarm() {
		try {
			const { eventID, regionID: regionId, regionURL: regionUrl, canProcess } = await this.eventHandleTop(
				this.currentFilterID
			);

			if (eventID != null && canProcess) {
				this.setHandlingEvent(true);
				this.setJoiningEventID(eventID);

				if (!regionId) {
					// Handle the event locally
					this.$router.push({
						path: `/site-monitor/${eventID}`
					});
				} else {
					// Handle the event remotely
					var loginSuccessful = await this.regionalLogin(regionId, formatRegionUrl(regionUrl));

					if (loginSuccessful) {
						this.$router.push({
							path: `/site-monitor/${eventID}`,
							query: {
								regionId
							}
						});
					} else {
						this.$notify({
							type: "error",
							title: "Unsuccessful Remote Login",
							text:
								"Unable to login to the remote region - please try again later, or contact support if the problem persists."
						});
					}
				}
			}
		} catch (err) {
			this.$notify({
				type: "error",
				title: "Process Top Alarm",
				text:
					"Unable to process the top alarm - please try again later, or contact support if the problem persists."
			});

			this.setJoiningEventID(null);
		} finally {
			this.setHandlingEvent(false);
		}
	}

	public selectFilter(filter: AlarmQueueFilter) {
		// when changing filters clear out any selected rows
		this.clearSelectedAlarms();

		// Update the store with the details of the filter
		const data = {
			filterId: filter.alarmQueueFilterID,
			regionId: filter.regionID
		};
		this.setActiveFilter(data);
	}

	public editFilter(filter: AlarmQueueFilter) {
		// @techdebt @todo delete after serverside implemetation
		const editedFilter: AlarmQueueFilterEditModel = {
			alarmQueueFilterID: filter.alarmQueueFilterID,
			title: filter.title,
			priorityValue: filter.priorityHigherThan,
			priorityOperator: this.capitalizeFirstLetter(filter.priorityHigherThanDDL),
			numberOfEventsValue: filter.numberOfEvents,
			numberOfEventsOperator: this.capitalizeFirstLetter(filter.numberOfEventsDDL),
			alarmAgeValue: filter.olderThan,
			alarmAgeOperator: this.capitalizeFirstLetter(filter.olderThanDDL),
			alarmTagID: filter.responseAlarmTypeID,
			groupID: filter.groups.length ? filter.groups[0].groupID : null,
			routingGroupID: filter.routingGroupID,
			shared: filter.shared,
			created: filter.created,
			combinedFilters: filter.combinedFilters,
			groups: filter.groups
		};

		this.$set(this, "editedFilter", editedFilter);
		this.updateFilter = true;
		this.setNewFilterModalShown(true);
	}

	public createNewFilter() {
		this.updateFilter = false;
		this.setNewFilterModalShown(true);
	}

	public manualPatrolOpen() {
		this.setManualPatrolShown(true);
	}

	public manualRaiseOpen() {
		// Show map if hidden.
		if (this.isToggleMapEnabled) {
			this.setHideMapFlag(false);
		}
		this.setManualRaiseShown(true);
	}

	public activityLogOpen() {
		this.setActivityLogShown(true);
	}

	public manualActionOpen(): void {
		if (!this.quickControlOpenNewWindow) {
			this.setManualActionShown(true);
			return;
		}

		window.open(`#/${AppRoutes.QuickControl}`, "", "location=yes,scrollbars=yes,status=yes,width=800,height=750");
	}

	public async newFilterSelected(filter: AlarmQueueFilter) {
		this.addingFilter = true;

		await this.addAlarmQueueFilter({
			filterId: filter.alarmQueueFilterID,
			regionId: filter.regionID || null
		});

		this.addingFilter = false;

		this.activeFilters.forEach(activeFilter => {
			if (
				activeFilter.alarmQueueFilterID == filter.alarmQueueFilterID &&
				activeFilter.regionID == filter.regionID
			) {
				this.selectFilter(activeFilter);
			}
		});
	}

	public async removeFilter(filter: AlarmQueueFilter) {
		let filterId = filter.alarmQueueFilterID;
		let regionId = filter.regionID || 0;

		await this.removeAlarmQueueFilter({ filterId, regionId });
	}

	public filterShortcut(event: any) {
		let selectedIndex = parseInt(event.srcKey);

		if (selectedIndex == 0) {
			selectedIndex = 10;
		}

		selectedIndex--;

		if (this.activeFilters.length > selectedIndex) {
			let selectedFilter = this.activeFilters[selectedIndex];
			!selectedFilter.hide && this.selectFilter(selectedFilter);
		}
	}

	public confirmDeleteFilter(filter: AlarmQueueFilter) {
		this.filterToDelete = filter;
		this.modalShow = true;
	}

	public async deleteFilter(id: number) {
		await this.deleteAlarmQueueFilter(id);
		this.hideConfirmationModal();
		this.setAllAvailableFilters();
	}

	public hideConfirmationModal() {
		this.modalShow = false;
	}

	public onCancelFilterEditing() {
		this.editedFilter = null;
	}

	private capitalizeFirstLetter(str: string) {
		return str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
	}

	public resetFilterSearch() {
		this.filterSearch = "";
	}

	private alarmPointsOpen() {
		this.setAlarmPointManagerOpen(true);
	}

	// Clears the selectedAlarms
	public clearSelectedAlarms() {
		this.setSelectedAlarms([]);
		// not totally needed but ensure no lag in the UI updating the SelectAll checkbox
		this.setAllAlarmsSelected(false);
	}

	// Called when the BulkHandle button is clicked.
	// calls the store action, inverting the value currently stored in the store state
	public toggleBulkHandle() {
		this.setBulkHandleShown(!this.bulkHandleShown);

		// when either showing or hiding the bulk realted controls clear out any selected rows
		this.clearSelectedAlarms();
	}

	/**
	 * Loads any metadata for the events within any of the queues. The metadata loaded
	 * will usually depend on whether or not a certain feature flag is enabled.
	 *
	 * Currently only tasks metadata is loaded, but any metadata you may need to load,
	 * that is related to events, and that needs to be accessed within the alarm queue page
	 * should be loaded here.
	 */
	private async fetchMetadataForEvents() {
		// If the tasks integration is enabled
		if (this.getFeature(["Integration", "Tasks"])) {
			// Get all responses for all active events in the queues, removing any nulls
			const activeEventResponses = this.events.flatMap(event => event.responses).filter(id => id != null);

			if (activeEventResponses.length > 0) {
				// ...and load any 'third party task system' metadata associated with those responses
				await this.loadMetadata({
					objects: activeEventResponses.map(responseId => ({
						objectId: responseId,
						objectType: "Responses"
					})),
					metadataType: "ThirdPartyTaskSystem"
				});
			}
		}
	}

	/**
	 * Permission and feature check
	 */
	public get canViewSystemEvents(): boolean {
		return (
			get(this.featuresList, ["Alarms", "EventQueue", "SystemEvents"]) &&
			(this.getPermissions.isAdmin || this.getPermissions.canViewSystemEvents)
		);
	}
}
